// Issue #329: Contexto do cliente ativo (seletor de cliente do contador).
//
// Diretriz Felix: "Registros nao se misturam. Contador deve acessar dados de
// seus clientes separadamente."
//
// Este contexto mantem o ID do cliente ativo selecionado no header, persistido
// em localStorage. Todas as telas (documentos, dashboard, exportacao) leem
// `clienteAtivoId` deste contexto e enviam como query param `cliente_id` nas
// chamadas de API. Quando null, mostra todos os clientes (com aviso).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { listClientes, type Cliente } from '../services/api';

const STORAGE_KEY = 'auranota_cliente_ativo';

export interface ClienteAtivoContextValue {
  clienteAtivoId: string | null;
  clienteAtivo: Cliente | null;
  clientes: Cliente[];
  loading: boolean;
  setClienteAtivoId: (id: string | null) => void;
  refreshClientes: () => Promise<void>;
}

const ClienteAtivoContext = createContext<ClienteAtivoContextValue | null>(null);

export function ClienteAtivoProvider({ children }: { children: ReactNode }) {
  const [clienteAtivoId, setClienteAtivoIdState] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshClientes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listClientes();
      setClientes(res.clientes ?? []);
    } catch {
      setClientes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshClientes();
  }, [refreshClientes]);

  const setClienteAtivoId = useCallback((id: string | null) => {
    setClienteAtivoIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Limpa o cliente ativo se ele nao existir mais na lista carregada.
  const clienteAtivo = useMemo(() => {
    if (!clienteAtivoId) return null;
    return clientes.find((c) => c.id === clienteAtivoId) ?? null;
  }, [clientes, clienteAtivoId]);

  useEffect(() => {
    if (clienteAtivoId && clientes.length > 0 && !clienteAtivo) {
      // O cliente selecionado nao esta mais na lista — reseta.
      setClienteAtivoId(null);
    }
  }, [clienteAtivoId, clientes, clienteAtivo, setClienteAtivoId]);

  const value = useMemo<ClienteAtivoContextValue>(
    () => ({
      clienteAtivoId,
      clienteAtivo,
      clientes,
      loading,
      setClienteAtivoId,
      refreshClientes,
    }),
    [clienteAtivoId, clienteAtivo, clientes, loading, setClienteAtivoId, refreshClientes],
  );

  return (
    <ClienteAtivoContext.Provider value={value}>
      {children}
    </ClienteAtivoContext.Provider>
  );
}

export function useClienteAtivo(): ClienteAtivoContextValue {
  const ctx = useContext(ClienteAtivoContext);
  if (!ctx) {
    throw new Error('useClienteAtivo deve ser usado dentro de ClienteAtivoProvider');
  }
  return ctx;
}
